import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Loader2, Trophy, Medal } from 'lucide-react';
import { fetchLeaderboard } from '../../services/gamesApi';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Global games leaderboard — supports All / Week / Today periods.
 * Read-only and public (no auth required), so unauthenticated visitors
 * can still see who's on top.
 */
const GamesLeaderboard = ({ language = 'en' }) => {
  const { t } = useLanguage();
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState({ entries: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLeaderboard(period, 20)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ entries: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const podiumIcon = (rank) => {
    if (rank === 0) return <Trophy className="w-4 h-4 text-yellow-500" />;
    if (rank === 1) return <Medal className="w-4 h-4 text-slate-400" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-amber-700" />;
    return <span className="text-xs font-mono text-muted-foreground w-4 text-center">{rank + 1}</span>;
  };

  return (
    <Card data-testid="games-leaderboard">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-yellow-500" />
            {t('games.lb.title')}
          </h3>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList className="grid grid-cols-3 w-full mb-3">
            <TabsTrigger value="today" data-testid="leaderboard-period-today" className="text-xs">
              {t('games.lb.today')}
            </TabsTrigger>
            <TabsTrigger value="week" data-testid="leaderboard-period-week" className="text-xs">
              {t('games.lb.week')}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="leaderboard-period-all" className="text-xs">
              {t('games.lb.allTime')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value={period} className="space-y-1">
            {loading && (
              <p className="text-xs text-muted-foreground text-center py-4">
                <Loader2 className="inline w-3 h-3 animate-spin mr-1" />
                {t('games.loading')}
              </p>
            )}
            {!loading && data.entries.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">
                {t('games.lb.noScores')}
              </p>
            )}
            {data.entries.map((e, i) => (
              <div
                key={e.user_id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/40"
                data-testid={`leaderboard-row-${i}`}
              >
                <div className="w-5 flex items-center justify-center">{podiumIcon(i)}</div>
                <Avatar className="w-7 h-7">
                  {e.avatar_url
                    ? <AvatarImage src={e.avatar_url} alt={e.name || ''} />
                    : null}
                  <AvatarFallback className="text-[10px]">
                    {(e.name || '??').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {e.name || t('games.lb.anonymous')}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {e.games_played} {t('games.lb.games')}
                  </p>
                </div>
                <p className="text-sm font-bold tabular-nums">{e.total_score}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default GamesLeaderboard;
